/** Lo que devuelve la API del organigrama. */

export type Organigrama = {
  id: number
  nombre: string
  tipo: "GENERAL" | "DEPARTAMENTAL"
  departamento_id: number | null
  descripcion: string | null
  orden: number
  activo: boolean
}

export type Nodo = {
  id: number
  organigrama_id: number
  padre_id: number | null
  cargo_id: number
  cargo: string | null
  nivel: string | null
  departamento: string | null
  persona_id: number | null
  ocupante: string | null
  vacante: boolean
  foto: string | null
  orden: number
  nota: string | null
  reporta_tambien_a: number[]
}

export type Arbol = Nodo & { hijos: Arbol[] }

/**
 * El acento de color por altura del puesto.
 *
 * ⚠ **Refuerza, no informa.** El nombre del cargo va escrito en la tarjeta; el color
 * sólo ayuda a leer el mapa de un vistazo. Si el color fuera el único portador del
 * nivel, el organigrama sería ilegible para quien no distingue esos tonos y en una
 * impresión en blanco y negro para todo el mundo (WCAG 1.4.1).
 */
export const ACENTO: Record<string, string> = {
  EMPRESA: "var(--primary-texto)",
  DIVISION: "var(--info)",
  DEPARTAMENTO: "var(--success)",
  POSICION: "var(--fg-muted)",
}

/**
 * Arma el bosque. Devuelve un ARRAY de raíces: el organigrama admite varias.
 *
 * ⚠ **Una posición cuyo padre no está en la lista se cuelga de la raíz**, no se
 * pierde. Con datos incoherentes —o con un filtro futuro— desaparecerían ramas
 * enteras sin que nada lo dijera.
 *
 * ⚠ **Y se corta a `PROFUNDIDAD`.** El servidor rechaza los ciclos, pero si alguna vez
 * uno llegara hasta aquí, este recorrido no volvería: el navegador se quedaría
 * colgado sin mensaje.
 */
const PROFUNDIDAD = 12

export function armarBosque(nodos: Nodo[]): Arbol[] {
  const porId = new Map<number, Arbol>(nodos.map((n) => [n.id, { ...n, hijos: [] }]))
  const raices: Arbol[] = []
  for (const nodo of porId.values()) {
    const padre = nodo.padre_id === null ? undefined : porId.get(nodo.padre_id)
    if (padre && padre.id !== nodo.id) padre.hijos.push(nodo)
    else raices.push(nodo)
  }
  const ordenar = (lista: Arbol[], nivel: number) => {
    if (nivel > PROFUNDIDAD) return
    lista.sort((a, b) => a.orden - b.orden || a.id - b.id)
    for (const n of lista) ordenar(n.hijos, nivel + 1)
  }
  ordenar(raices, 0)
  return raices
}

/** Las iniciales, para cuando no hay foto. Es lo que hace la ficha de persona. */
export function iniciales(nombre: string | null): string {
  if (!nombre) return "—"
  const partes = nombre.trim().split(/\s+/)
  const primera = partes[0]?.[0] ?? ""
  const segunda = partes.length > 1 ? (partes[partes.length - 1]?.[0] ?? "") : ""
  return (primera + segunda).toUpperCase() || "—"
}
